import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { debounce, uuid } from '@carvana/purchase-utils';

const Input = styled.input`
  border: 1px solid #999;
  padding: 0.5rem;
  width: 300px;
`;

const NoSuggestions = styled.div`
  color: #999;
  padding: 0.5rem;
  text-decoration: underline;
  cursor: pointer;
`;

const Suggestions = styled.ul`
  border: 1px solid #999;
  border-top-width: 0;
  list-style: none;
  margin-top: 0;
  overflow-y: auto;
  padding-left: 0;
  width: calc(300px + 1rem);
  cursor: pointer;
`;

const Suggestion = styled.li`
  padding: 0.5rem;

  background-color: ${props => props.active && `rgba(0, 0, 0, 0.14)`};
  font-weight: ${props => props.active && 700};

  &:hover {
    background-color: rgba(0, 0, 0, 0.14);
    font-weight: 700;
  }
`;

const EnterKeyCode = 13;
const UpArrowKeyCode = 38;
const DownArrowKeyCode = 40;

class Autocomplete extends Component {
  static propTypes = {
    suggestions: PropTypes.instanceOf(Array),
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    dropdown: PropTypes.func,
    input: PropTypes.node,
    noSuggestions: PropTypes.node,
    formatInput: PropTypes.func
  };

  static defaultProps = {
    suggestions: [],
    onChange: () => {},
    onSelect: () => {},
    dropdown: null,
    input: null,
    noSuggestions: null,
    formatInput: input => input
  };

  constructor(props) {
    super(props);

    this.state = {
      activeSuggestion: 0,
      showSuggestions: false,
      userInput: ''
    };

    const { onChange } = this.props;
    this.onChanged = debounce(onChange, 300);
  }

  onChange = e => {
    e.persist();
    const userInput = e.currentTarget.value;
    if (userInput && userInput.length > 2) this.onChanged(userInput);

    this.setState({
      activeSuggestion: 0,
      showSuggestions: userInput && userInput.length > 2,
      userInput: e.currentTarget.value
    });
  };

  onClick = e => {
    const { onSelect, suggestions, formatInput } = this.props;

    onSelect(
      suggestions[
        suggestions.findIndex(
          x => formatInput(x) === e.currentTarget.innerText
        ) || 0
      ]
    );
    this.setState({
      activeSuggestion: 0,
      showSuggestions: false,
      userInput: e.currentTarget.innerText
    });
  };

  onKeyDown = e => {
    const { suggestions } = this.props;
    const { activeSuggestion } = this.state;

    if (e.currentTarget.value) {
      if (e.keyCode === EnterKeyCode) {
        if (suggestions && suggestions[activeSuggestion]) {
          const { formatInput, onSelect } = this.props;
          this.setState({
            activeSuggestion,
            showSuggestions: false,
            userInput: suggestions
              ? formatInput(suggestions[activeSuggestion])
              : e.currentTarget.value
          });

          onSelect(suggestions[activeSuggestion]);
          e.preventDefault();
        }
      } else if (e.keyCode === UpArrowKeyCode) {
        if (activeSuggestion === 0) return;

        this.setState({ activeSuggestion: activeSuggestion - 1 });
      } else if (e.keyCode === DownArrowKeyCode) {
        if (suggestions.length === 1) return;

        this.setState({ activeSuggestion: activeSuggestion + 1 });
      }
    }
  };

  render() {
    const {
      onChange,
      onClick,
      onKeyDown,
      props: { suggestions, noSuggestions, input, formatInput, dropdown },
      state: { activeSuggestion, showSuggestions, userInput }
    } = this;

    let suggestionsListComponent;
    if (showSuggestions && userInput) {
      if (suggestions.length) {
        suggestionsListComponent = dropdown ? (
          dropdown({ ...this.state, onClick })
        ) : (
          <Suggestions>
            {suggestions.map((suggestion, index) => (
              <Suggestion
                active={index === activeSuggestion}
                key={uuid()}
                onClick={onClick}
              >
                {formatInput(suggestion)}
              </Suggestion>
            ))}
          </Suggestions>
        );
      } else {
        suggestionsListComponent = noSuggestions || (
          <NoSuggestions>
            <em>No suggestions</em>
          </NoSuggestions>
        );
      }
    }

    return (
      <Fragment>
        {input ? (
          React.cloneElement(input, {
            props: this.props,
            onChange,
            onKeyDown,
            value: userInput
          })
        ) : (
          <Input
            autoFocus
            type="text"
            onChange={onChange}
            onKeyDown={onKeyDown}
            value={userInput}
          />
        )}
        {suggestionsListComponent}
      </Fragment>
    );
  }
}

export default Autocomplete;
